import { useCallback, useMemo, useRef, useState } from 'react';

import { gql } from '@apollo/client';
import type { ErrorLike } from '@apollo/client';
import type { Reference, StoreObject } from '@apollo/client/cache';
import { useMutation } from '@apollo/client/react';

import { Lots_Status_Enum_Enum } from '@graphql/__generated__/graphql';
import { PortalPublicationErrorDialog } from '@modules/_shared/components/PortalPublicationErrorDialog';
import { PortalPublicationSnackbar } from '@modules/_shared/components/PortalPublicationSnackbar';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { buildLotChangeActivities } from '@modules/_shared/utils/lotChangeRules';
import {
  TOGGLE_PORTAL_LISTING,
  UPDATE_LISTING_TRACKED,
} from '@modules/listings/lotsQueries';

type PortalWarning = {
  message: string;
  raw: string;
  timestamp: number;
  type: string;
};

type Props = {
  onPortalListingChange?: (portalId: string, live: boolean) => void;
};

export const useTogglePortalListing = ({
  onPortalListingChange,
}: Props = {}) => {
  const [toggleError, setToggleError] = useState<null | ErrorLike>(null);
  const [toggleWarnings, setToggleWarnings] = useState<null | PortalWarning[]>(
    null,
  );
  const activePortalRef = useRef<{
    portal_id: string;
    lot_id: string;
  } | null>(null);
  const { me } = useAppData();
  const [updateListingTracked] = useMutation(UPDATE_LISTING_TRACKED);
  const [togglePortalListingMutation, { loading: isToggleLoading }] =
    useMutation(TOGGLE_PORTAL_LISTING, {
      onError: e => setToggleError(e),
      onCompleted: onCompletedData => {
        if (!onCompletedData?.toggle_portal_listing) {
          return;
        }

        if (
          onCompletedData.toggle_portal_listing.last_publication_warnings !=
          null
        ) {
          setToggleWarnings(
            onCompletedData.toggle_portal_listing.last_publication_warnings,
          );
        }

        onPortalListingChange?.(
          onCompletedData.toggle_portal_listing.portal_id,
          onCompletedData.toggle_portal_listing.live,
        );

        // Will update the status of the listing on the first publication.
        const lot = onCompletedData.toggle_portal_listing.lot;
        if (lot != null && lot.status === Lots_Status_Enum_Enum.Draft) {
          const set = { status: Lots_Status_Enum_Enum.Available };
          const activities = buildLotChangeActivities(
            { status: lot.status },
            set,
            {
              lotId: lot.id,
              tenantId: lot.tenant_id,
              // agent can't set the created_by column of activities, it is a preset in Hasura
              sessionUserId: (!me?.is_admin ? null : me?.id) ?? null,
            },
          );
          // The status flip runs in a separate Hasura request from the
          // toggle action — if it fails the lot is published but
          // `lot.status` stays `draft`. Surface the error via
          // setToggleError so the user can retry rather than silently
          // leaving the lot in an inconsistent state.
          updateListingTracked({
            variables: {
              id: lot.id,
              set,
              activities,
              hasActivities: activities.length > 0,
            },
            onError: e => setToggleError(e),
          });
        }
      },
      update: (cache, { data: togglePortalData }) => {
        if (!togglePortalData?.toggle_portal_listing) {
          return;
        }

        // Because the action doesn't return portal_listing type but PortalListing type
        cache.modify({
          id: `portal_listings:${togglePortalData.toggle_portal_listing.id}`,
          fields: {
            live() {
              return togglePortalData.toggle_portal_listing?.live ?? false;
            },
          },
        });

        const portalListing = togglePortalData.toggle_portal_listing;

        if (portalListing?.lot != null) {
          cache.modify({
            id: `lots:${portalListing.lot.id}`,
            fields: {
              portal_listings(existingPortalListings = [], { readField }) {
                const newPortalListingRef = cache.writeFragment({
                  data: portalListing,
                  fragment: gql`
                    fragment NewPortalListing on portal_listings {
                      __typename
                      id
                      live
                      portal_id
                      portal {
                        id
                        name
                        active
                      }
                      lot {
                        id
                        status
                      }
                    }
                  `,
                });

                let didInsertPortalListing = false;

                const dedupedPortalListings = existingPortalListings.flatMap(
                  (portalListingRef: Reference | StoreObject) => {
                    const isCurrentPortalListing =
                      readField('id', portalListingRef) === portalListing.id ||
                      readField('portal_id', portalListingRef) ===
                        portalListing.portal_id;

                    if (!isCurrentPortalListing) {
                      return [portalListingRef];
                    }

                    if (didInsertPortalListing) {
                      return [];
                    }

                    didInsertPortalListing = true;
                    return [newPortalListingRef];
                  },
                );

                return didInsertPortalListing
                  ? dedupedPortalListings
                  : [...dedupedPortalListings, newPortalListingRef];
              },
            },
          });
        }
      },
    });

  const WarningSnackbar = useMemo(
    () =>
      toggleWarnings && (
        <PortalPublicationSnackbar
          warnings={toggleWarnings}
          onClose={() => setToggleWarnings(null)}
        />
      ),
    [toggleWarnings],
  );

  const ErrorDialog = useMemo(
    () => (
      <PortalPublicationErrorDialog
        error={toggleError}
        onClose={() => setToggleError(null)}
        onRetryWithoutFTPCheck={async () => {
          if (activePortalRef.current == null) {
            return;
          }

          // Rejects on failure (Apollo Client 4 re-throws after `onError`),
          // which keeps the dialog open.
          const result = await togglePortalListingMutation({
            variables: {
              portal_id: activePortalRef.current.portal_id,
              lot_id: activePortalRef.current.lot_id,
              skip_ftp_check: true,
            },
          });

          // The action's return type is nullable: a null payload without a
          // GraphQL error would resolve and close the dialog as a success.
          if (result.data?.toggle_portal_listing == null) {
            throw new Error('Failed to publish listing');
          }
        }}
        loading={isToggleLoading}
      />
    ),
    [toggleError, togglePortalListingMutation, isToggleLoading],
  );

  const togglePortalListing = useCallback<typeof togglePortalListingMutation>(
    options => {
      activePortalRef.current = {
        portal_id: options?.variables?.portal_id ?? '',
        lot_id: options?.variables?.lot_id ?? '',
      };

      return togglePortalListingMutation(options);
    },
    [togglePortalListingMutation],
  );

  return {
    togglePortalListing,
    WarningSnackbar,
    ErrorDialog,
    // Exposes setters for the error and warnings states if needed.
    setToggleError,
    setToggleWarnings,
  };
};
